import React, { useEffect, useState } from 'react';
import { Zap, ShieldCheck, MessageSquare, Plus, Trash2, CornerDownRight } from 'lucide-react';
import { CommanServices } from '@/services/commanServices';

const SECTIONS = [
  { value: 'response_action', label: 'Response Action', icon: Zap },
  { value: 'acknowledgment', label: 'Verification', icon: ShieldCheck },
  { value: 'status_update', label: 'Status Update', icon: MessageSquare },
];

const emptyInject = (section = 'response_action') => ({
  section,
  title: '',
  assignee: null,
  slaMinutes: 5,
  fireOffsetMinutes: 0,
  tasks: [],
  escalation: null,
});

// Recursive editor for a single escalation rule: "if not completed in X min -> inject card"
const RuleNode = ({ value, onChange, appUsers, depth, maxDepth }) => {
  const enabled = !!value;

  const toggle = (on) => onChange(on ? { timeoutMinutes: 5, inject: emptyInject() } : null);
  const patch = (p) => onChange({ ...value, ...p });
  const patchInject = (p) => onChange({ ...value, inject: { ...value.inject, ...p } });

  const inject = value?.inject || emptyInject();
  const isResponse = inject.section === 'response_action';

  const updateTask = (i, text) => {
    const tasks = [...(inject.tasks || [])];
    tasks[i] = text;
    patchInject({ tasks });
  };
  const addTask = () => patchInject({ tasks: [...(inject.tasks || []), ''] });
  const removeTask = (i) => patchInject({ tasks: (inject.tasks || []).filter((_, idx) => idx !== i) });

  return (
    <div className={depth > 0 ? 'border-l-2 border-amber-200 pl-3 ml-1' : ''}>
      <label className="flex items-center gap-2 cursor-pointer select-none" data-testid={`escalation-toggle-${depth}`}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => toggle(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
        />
        <span className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
          {depth > 0 && <CornerDownRight className="h-3.5 w-3.5 text-amber-500" />}
          If not completed in time → inject another card
        </span>
      </label>

      {enabled && (
        <div className="mt-3 space-y-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
          {/* Timeout */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-700">If not completed within</span>
            <input
              type="number" min="1"
              data-testid={`escalation-timeout-${depth}`}
              value={value.timeoutMinutes}
              onChange={(e) => patch({ timeoutMinutes: Math.max(1, parseInt(e.target.value || '1', 10)) })}
              className="h-8 w-20 rounded border border-gray-300 px-2 text-sm focus:border-amber-400 focus:outline-none"
            />
            <span className="text-gray-700">min, then inject:</span>
          </div>

          {/* Section */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-medium text-gray-500 uppercase">Section</label>
              <select
                data-testid={`escalation-section-${depth}`}
                value={inject.section}
                onChange={(e) => patchInject({ section: e.target.value, tasks: e.target.value === 'response_action' ? inject.tasks : [] })}
                className="mt-1 h-9 w-full rounded border border-gray-300 px-2 text-sm bg-white focus:border-amber-400 focus:outline-none"
              >
                {SECTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-medium text-gray-500 uppercase">SLA (min)</label>
              <input
                type="number" min="1"
                data-testid={`escalation-sla-${depth}`}
                value={inject.slaMinutes}
                onChange={(e) => patchInject({ slaMinutes: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                className="mt-1 h-9 w-full rounded border border-gray-300 px-2 text-sm focus:border-amber-400 focus:outline-none"
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="text-[11px] font-medium text-gray-500 uppercase">Title</label>
            <input
              type="text"
              data-testid={`escalation-title-${depth}`}
              value={inject.title}
              onChange={(e) => patchInject({ title: e.target.value })}
              placeholder="e.g. Escalate evacuation to Crew 2"
              className="mt-1 h-9 w-full rounded border border-gray-300 px-2 text-sm focus:border-amber-400 focus:outline-none"
            />
          </div>

          {/* Assignee */}
          <div>
            <label className="text-[11px] font-medium text-gray-500 uppercase">Assign to</label>
            <select
              data-testid={`escalation-assignee-${depth}`}
              value={inject.assignee?.userId || ''}
              onChange={(e) => {
                const u = (appUsers || []).find((x) => x.id === e.target.value);
                patchInject({ assignee: u ? { userId: u.id, userName: u.name } : null });
              }}
              className="mt-1 h-9 w-full rounded border border-gray-300 px-2 text-sm bg-white focus:border-amber-400 focus:outline-none"
            >
              <option value="">Select user…</option>
              {(appUsers || []).map((u) => (
                <option key={u.id} value={u.id}>{u.name}{u.position ? ` (${u.position})` : ''}</option>
              ))}
            </select>
          </div>

          {/* Tasks (response action only) */}
          {isResponse && (
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-medium text-gray-500 uppercase">Tasks</label>
                <button type="button" onClick={addTask} data-testid={`escalation-add-task-${depth}`} className="text-xs text-amber-700 font-semibold flex items-center gap-1 hover:underline">
                  <Plus className="h-3 w-3" /> Add task
                </button>
              </div>
              <div className="mt-1 space-y-1.5">
                {(inject.tasks || []).map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={t}
                      onChange={(e) => updateTask(i, e.target.value)}
                      placeholder={`Task ${i + 1}`}
                      className="h-8 flex-1 rounded border border-gray-300 px-2 text-sm focus:border-amber-400 focus:outline-none"
                    />
                    <button type="button" onClick={() => removeTask(i)} className="p-1 text-gray-400 hover:text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {(inject.tasks || []).length === 0 && <p className="text-[11px] text-gray-400">No tasks yet.</p>}
              </div>
            </div>
          )}

          {/* Recursive chaining */}
          {depth < maxDepth && (
            <div className="pt-2 border-t border-amber-200">
              <RuleNode
                value={inject.escalation}
                onChange={(v) => patchInject({ escalation: v })}
                appUsers={appUsers}
                depth={depth + 1}
                maxDepth={maxDepth}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const EscalationRuleBuilder = ({ value, onChange, maxDepth = 3 }) => {
  const [appUsers, setAppUsers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await CommanServices.getAppUsers();
        if (res?.status) setAppUsers(res.data || []);
      } catch (e) { /* noop */ }
    })();
  }, []);

  return (
    <div className="rounded-lg border border-amber-200 bg-white p-3" data-testid="escalation-rule-builder">
      <div className="flex items-center gap-2 mb-2">
        <Zap className="h-4 w-4 text-amber-600" />
        <span className="text-sm font-bold text-amber-900">Conditional Logic</span>
      </div>
      <RuleNode value={value} onChange={onChange} appUsers={appUsers} depth={0} maxDepth={maxDepth} />
    </div>
  );
};

export default EscalationRuleBuilder;
